import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { get_product_processing, get_undistributed_products, get_finished_product_stock_report } from "../../../../Utility/Api";
import { NaviGate, shop_id } from "../../../../Utility/function";
import { WarehouseContext } from "../../../../Utility/UserContext";
import { Calendar } from "lucide-react";
import moment from "moment";
import "./../table.css";

const FormattedDateInput = ({ id, label, value, onChange, min, max }) => {
    return (
        <div className="form-floating position-relative">
            <input
                type="date"
                id={id}
                value={value || ''}
                min={min}
                max={max}
                onChange={onChange}
                style={{
                    opacity: 0,
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    cursor: 'pointer',
                    zIndex: 10
                }}
            />
            <div
                className="form-control d-flex align-items-center justify-content-between"
                style={{
                    paddingTop: '1.625rem',
                    paddingBottom: '0.625rem',
                    height: '58px',
                    pointerEvents: 'none',
                    backgroundColor: '#fff'
                }}
            >
                <span className={value ? "text-dark fw-medium" : "text-muted"}>
                    {value ? moment(value).format('DD/MM/YYYY') : 'DD/MM/YYYY'}
                </span>
                <Calendar size={16} className="text-secondary ms-2 shrink-0" />
            </div>
            <label htmlFor={id} style={{ zIndex: 5 }}>{label}</label>
        </div>
    );
};

const MONTHS = [
    { value: 1, label: "January", short: "Jan" },
    { value: 2, label: "February", short: "Feb" },
    { value: 3, label: "March", short: "Mar" },
    { value: 4, label: "April", short: "Apr" },
    { value: 5, label: "May", short: "May" },
    { value: 6, label: "June", short: "Jun" },
    { value: 7, label: "July", short: "Jul" },
    { value: 8, label: "August", short: "Aug" },
    { value: 9, label: "September", short: "Sep" },
    { value: 10, label: "October", short: "Oct" },
    { value: 11, label: "November", short: "Nov" },
    { value: 12, label: "December", short: "Dec" },
];

const FinishedProductReport = () => {
    const navigate = useNavigate();
    const { showLoader, hideLoader, shopItem } = useContext(WarehouseContext);
    const [processes, setProcesses] = useState([]);
    const [undistributedBatches, setUndistributedBatches] = useState({});
    const [searchText, setSearchText] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [expandedRows, setExpandedRows] = useState({});
    
    // Tab State: 'date_summary', 'monthly_summary', 'stock_summary', or 'batch_report'
    const [activeTab, setActiveTab] = useState("date_summary");
    const [stockSummary, setStockSummary] = useState([]);

    // Date-Wise Summary States
    const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
    const [dateStockSummary, setDateStockSummary] = useState([]);
    const [dateLoading, setDateLoading] = useState(false);
    const [dateSearchText, setDateSearchText] = useState("");
    const [expandedDateRows, setExpandedDateRows] = useState({});
    
    // Monthly Summary States
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [monthlyStockSummary, setMonthlyStockSummary] = useState([]);
    const [monthlyLoading, setMonthlyLoading] = useState(false);
    const [monthlySearchText, setMonthlySearchText] = useState("");
    const [expandedMonthlyRows, setExpandedMonthlyRows] = useState({});

    const [companyInfo, setCompanyInfo] = useState({
        name: "ZTR Masala",
        address: "Business Address",
        phone: "",
        email: ""
    });

    useEffect(() => {
        if (shopItem) {
            setCompanyInfo({
                name: shopItem?.title || "ZTR Masala",
                address: shopItem?.Address || "Business Address",
                phone: shopItem?.phone1 || "",
                email: shopItem?.email || ""
            });
        }
    }, [shopItem]);

    // Initial load
    useEffect(() => {
        fetchDateStockSummary(selectedDate);
    }, []);

    // Fetch Date Stock Summary when tab is active or selectedDate/shop changes
    useEffect(() => {
        if (activeTab === "date_summary") {
            fetchDateStockSummary(selectedDate);
        }
    }, [activeTab, selectedDate, shopItem]);

    // Fetch Monthly Stock Summary when tab is active or year/month/shop changes
    useEffect(() => {
        if (activeTab === "monthly_summary") {
            fetchMonthlyStockSummary(selectedYear, selectedMonth);
        }
    }, [activeTab, selectedYear, selectedMonth, shopItem]);

    // Fetch Batch / Period Stock Summary when tab is active or shop changes
    useEffect(() => {
        if (activeTab === "batch_report" || activeTab === "stock_summary") {
            fetchData();
        }
    }, [activeTab, shopItem]);

    const fetchDateStockSummary = async (dateVal = selectedDate) => {
        try {
            showLoader();
            setDateLoading(true);
            const currentShopId = shopItem?.shop_id || shopItem?.id || shop_id || "0";

            const summaryRes = await get_finished_product_stock_report({
                shop_id: currentShopId,
                date: dateVal
            });

            if (summaryRes.data && summaryRes.data.status === "success") {
                setDateStockSummary(summaryRes.data.data || []);
            } else {
                setDateStockSummary([]);
            }
        } catch (e) {
            console.error("Failed to fetch date-wise stock summary", e);
            setDateStockSummary([]);
        } finally {
            setDateLoading(false);
            hideLoader();
        }
    };

    const fetchMonthlyStockSummary = async (year = selectedYear, month = selectedMonth) => {
        try {
            showLoader();
            setMonthlyLoading(true);
            const currentShopId = shopItem?.shop_id || shopItem?.id || shop_id || "0";
            const monthStr = String(month).padStart(2, '0');
            const mFromDate = `${year}-${monthStr}-01`;
            const mToDate = moment(mFromDate).endOf('month').format('YYYY-MM-DD');

            const summaryRes = await get_finished_product_stock_report({
                shop_id: currentShopId,
                from_date: mFromDate,
                to_date: mToDate
            });

            if (summaryRes.data && summaryRes.data.status === "success") {
                setMonthlyStockSummary(summaryRes.data.data || []);
            } else {
                setMonthlyStockSummary([]);
            }
        } catch (e) {
            console.error("Failed to fetch monthly stock summary", e);
            setMonthlyStockSummary([]);
        } finally {
            setMonthlyLoading(false);
            hideLoader();
        }
    };

    const fetchData = async () => {
        try {
            showLoader();
            const currentShopId = shopItem?.shop_id || shopItem?.id || shop_id || "0";
            const params = {
                shop_id: currentShopId
            };
            if (fromDate) params.from_date = fromDate;
            if (toDate) params.to_date = toDate;
            const response = await get_product_processing(params);
            
            // Fetch Stock Summary
            if (fromDate && toDate) {
                try {
                    const summaryRes = await get_finished_product_stock_report(params);
                    if (summaryRes.data && summaryRes.data.status === "success") {
                        setStockSummary(summaryRes.data.data || []);
                    }
                } catch (e) {
                    console.error("Failed to fetch stock summary", e);
                }
            } else {
                setStockSummary([]);
            }

            const undistResponse = await get_undistributed_products({
                shop_id: currentShopId,
                page_size: 100000
            });

            const undistMap = {};
            if (undistResponse.data && undistResponse.data.data) {
                undistResponse.data.data.forEach(item => {
                    if (item.batch_no) {
                        undistMap[String(item.batch_no)] = parseFloat(item.remaining || 0);
                    }
                });
            }
            setUndistributedBatches(undistMap);

            if (response.data && response.data.data) {
                const processedItems = [];
                response.data.data.forEach(item => {
                    const statusNum = parseInt(item.status);
                    
                    if (statusNum === 3 || statusNum === 4) {
                        const totalQty = parseFloat(item.manufactured_qty || item.desired_qty || 0);
                        const remainingBulk = undistMap[String(item.batch_no)] !== undefined
                            ? undistMap[String(item.batch_no)]
                            : (statusNum === 3 ? parseFloat(item.avl_qty || 0) : 0);
                        
                        const distributedQty = Math.max(0, totalQty - remainingBulk);
                        
                        if (distributedQty > 0) {
                            processedItems.push({
                                ...item,
                                id: `${item.id}_dist`,
                                status: "4", // Distributed
                                manufactured_qty: distributedQty,
                                desired_qty: distributedQty,
                                avl_qty: distributedQty,
                                isLoose: false
                            });
                        }
                        
                        if (remainingBulk > 0) {
                            processedItems.push({
                                ...item,
                                id: `${item.id}_undist`,
                                status: "3", // Completed (Loose Product)
                                manufactured_qty: remainingBulk,
                                desired_qty: remainingBulk,
                                avl_qty: remainingBulk,
                                isLoose: true
                            });
                        }
                        
                        if (distributedQty <= 0 && remainingBulk <= 0) {
                            processedItems.push({
                                ...item,
                                isLoose: statusNum === 3
                            });
                        }
                    }
                });
                setProcesses(processedItems);
            }
            hideLoader();
        } catch (error) {
            hideLoader();
            console.error("Error fetching finished products data:", error);
        }
    };

    const getStatusLabel = (status) => {
        const num = parseInt(status);
        if (num === 0) return "Pending";
        if (num === 1) return "In Progress";
        if (num === 2) return "Manufacturing";
        if (num === 3) return "Completed";
        if (num === 4) return "Distributed";
        return status;
    };

    const getStatusClass = (status) => {
        const num = parseInt(status);
        if (num === 0) return "text-warning fw-bold";
        if (num === 1) return "text-info fw-bold";
        if (num === 2) return "text-primary fw-bold";
        if (num === 3) return "text-success fw-bold";
        if (num === 4) return "text-success fw-bold";
        return "";
    };

    const applyFilters = () => {
        let filtered = [...processes];

        // Status filter
        if (selectedStatus) {
            filtered = filtered.filter(item => String(item.status) === String(selectedStatus));
        }

        // Search text
        if (searchText) {
            const search = searchText.toLowerCase();
            filtered = filtered.filter(item =>
                (item.product_title && item.product_title.toLowerCase().includes(search)) ||
                (item.batch_no && item.batch_no.toLowerCase().includes(search))
            );
        }

        return filtered;
    };

    const filteredData = applyFilters();

    const getGroupedData = (data) => {
        const groups = {};
        data.forEach(item => {
            const key = `${item.p_id}_${item.status}`;
            if (!groups[key]) {
                groups[key] = {
                    ...item,
                    batch_numbers: [item.batch_no].filter(Boolean),
                    qty: parseFloat(item.manufactured_qty || item.desired_qty || 0),
                    avl_qty: parseFloat(item.avl_qty || 0),
                    overall_price: parseFloat(item.overall_price || 0),
                    latest_date: item.create_date,
                    batches: [item]
                };
            } else {
                groups[key].qty += parseFloat(item.manufactured_qty || item.desired_qty || 0);
                groups[key].avl_qty += parseFloat(item.avl_qty || 0);
                groups[key].overall_price += parseFloat(item.overall_price || 0);
                if (item.batch_no && !groups[key].batch_numbers.includes(item.batch_no)) {
                    groups[key].batch_numbers.push(item.batch_no);
                }
                if (item.create_date && (!groups[key].latest_date || item.create_date > groups[key].latest_date)) {
                    groups[key].latest_date = item.create_date;
                }
                groups[key].batches.push(item);
            }
        });

        return Object.values(groups).map(g => {
            const isLoose = parseInt(g.status) === 3;
            return {
                ...g,
                batch_no: g.batch_numbers.join(", "),
                create_date: g.latest_date,
                manufactured_qty: g.qty,
                desired_qty: g.qty,
                avl_qty: g.avl_qty,
                isLoose: isLoose
            };
        });
    };

    const groupedData = getGroupedData(filteredData);

    const toggleRow = (id) => {
        setExpandedRows(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const toggleDateRow = (id) => {
        setExpandedDateRows(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const toggleMonthlyRow = (id) => {
        setExpandedMonthlyRows(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    // Filtered lists
    const filteredDateStock = dateStockSummary.filter(item => {
        if (!dateSearchText.trim()) return true;
        return (item.product_title || "").toLowerCase().includes(dateSearchText.toLowerCase().trim());
    });

    const totalDateClosingStock = filteredDateStock.reduce(
        (sum, item) => sum + parseFloat(item.closing_stock || 0), 0
    );
    const totalDateClosingCost = filteredDateStock.reduce(
        (sum, item) => sum + parseFloat(item.closing_cost || 0), 0
    );

    const filteredMonthlyStock = monthlyStockSummary.filter(item => {
        if (!monthlySearchText.trim()) return true;
        return (item.product_title || "").toLowerCase().includes(monthlySearchText.toLowerCase().trim());
    });

    const totalMonthlyClosingStock = filteredMonthlyStock.reduce(
        (sum, item) => sum + parseFloat(item.closing_stock || 0), 0
    );
    const totalMonthlyClosingCost = filteredMonthlyStock.reduce(
        (sum, item) => sum + parseFloat(item.closing_cost || 0), 0
    );

    const currentYear = new Date().getFullYear();
    const yearOptions = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

    const totalItems = groupedData.length;
    const totalOverallPrice = groupedData.reduce((sum, item) => sum + parseFloat(item.overall_price || 0), 0);

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        const printContent = document.getElementById('printable-finished-product-report').innerHTML;

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Finished Product Stock Report</title>
                <style>
                    @media print {
                        @page {
                            size: A4 portrait;
                            margin: 0.5in;
                        }
                        body {
                            font-family: Arial, sans-serif;
                            font-size: 11pt;
                            color: #000;
                            background: #fff;
                        }
                        .print-container {
                            width: 100%;
                            max-width: 21cm;
                            margin: 0 auto;
                        }
                        .print-header {
                            text-align: center;
                            margin-bottom: 20px;
                            border-bottom: 2px solid #000;
                            padding-bottom: 15px;
                        }
                        .print-header h1 {
                            font-size: 18pt;
                            margin: 0;
                            color: #000;
                        }
                        .print-header p {
                            margin: 4px 0;
                            font-size: 10pt;
                        }
                        .report-info {
                            margin: 15px 0;
                            padding: 10px;
                            background-color: #f8f9fa;
                            border-radius: 5px;
                            display: flex;
                            justify-content: space-between;
                        }
                        .report-table {
                            width: 100%;
                            border-collapse: collapse;
                            margin: 15px 0;
                            font-size: 10pt;
                        }
                        .report-table th, .report-table td {
                            border: 1px solid #ddd;
                            padding: 8px;
                            text-align: left;
                        }
                        .report-table th {
                            background-color: #f2f2f2;
                            font-weight: bold;
                        }
                        .report-totals {
                            margin-top: 20px;
                            padding: 15px;
                            border: 1px solid #ddd;
                            border-radius: 5px;
                            background-color: #f9f9f9;
                        }
                        .total-row {
                            display: flex;
                            justify-content: space-between;
                            margin: 5px 0;
                        }
                        .total-amount {
                            font-weight: bold;
                            font-size: 12pt;
                        }
                        .footer {
                            margin-top: 30px;
                            text-align: center;
                            font-size: 9pt;
                            color: #6c757d;
                        }
                    }
                </style>
            </head>
            <body>
                ${printContent}
                <script>
                    window.onload = function() {
                        window.print();
                        setTimeout(function() {
                            window.close();
                        }, 100);
                    };
                </script>
            </body>
            </html>
        `);
        printWindow.document.close();
    };

    return (
        <div className="page-content py-1">
            <NaviGate />
            <div className="card p-3 mx-2 overflow-y-scroll" style={{ minHeight: "90vh" }}>
                {/* Tab Switcher */}
                <div className="d-flex justify-content-center mb-4 border-bottom pb-3">
                    <div className="btn-group" role="group">
                        <button 
                            type="button" 
                            className={`btn ${activeTab === "date_summary" ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => setActiveTab("date_summary")}
                        >
                            <i className="bi bi-calendar-event me-1"></i> Date Wise Summary
                        </button>
                        <button 
                            type="button" 
                            className={`btn ${activeTab === "monthly_summary" ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => setActiveTab("monthly_summary")}
                        >
                            <i className="bi bi-calendar3 me-1"></i> Monthly Summary
                        </button>
                        <button 
                            type="button" 
                            className={`btn ${activeTab === "stock_summary" ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => setActiveTab("stock_summary")}
                        >
                            <i className="bi bi-calendar-range me-1"></i> Stock Summary (By Period)
                        </button>
                        <button 
                            type="button" 
                            className={`btn ${activeTab === "batch_report" ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => setActiveTab("batch_report")}
                        >
                            <i className="bi bi-gear-wide-connected me-1"></i> Batch Process Report
                        </button>
                    </div>
                </div>

                {/* Toolbar / Filters based on activeTab */}
                {activeTab === "date_summary" ? (
                    <div className="d-flex mb-4 border-bottom justify-content-between align-items-center flex-wrap pb-3" style={{ width: "100%" }}>
                        <h5 className="text-start w-auto fw-bold mb-0 text-uppercase">
                            Finished Product Date-Wise Summary
                        </h5>
                        <div className="d-flex gap-2 align-items-center flex-wrap ms-auto">
                            {/* Date Selector */}
                            <div style={{ width: "170px" }}>
                                <FormattedDateInput
                                    id="date_summary_select"
                                    label="Select Date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                />
                            </div>

                            {/* Search Bar */}
                            <div style={{ width: "230px" }}>
                                <div className="form-floating">
                                    <input
                                        className="form-control"
                                        id="date_search"
                                        value={dateSearchText}
                                        onChange={(e) => setDateSearchText(e.target.value)}
                                        placeholder="Search Product"
                                    />
                                    <label htmlFor="date_search">Search Product</label>
                                </div>
                            </div>

                            {/* Print Button */}
                            <button
                                type="button"
                                className="btn btn-dark p-3"
                                onClick={handlePrint}
                                style={{ height: "58px" }}
                            >
                                <i className="bi bi-printer me-1"></i> Print
                            </button>
                        </div>
                    </div>
                ) : activeTab === "monthly_summary" ? (
                    <div className="d-flex mb-4 border-bottom justify-content-between align-items-center flex-wrap pb-3" style={{ width: "100%" }}>
                        <h5 className="text-start w-auto fw-bold mb-0 text-uppercase">
                            Finished Product Monthly Summary
                        </h5>
                        <div className="d-flex gap-2 align-items-center flex-wrap ms-auto">
                            {/* Year Selector Dropdown */}
                            <div style={{ width: "130px" }}>
                                <div className="form-floating">
                                    <select
                                        className="form-select fw-bold"
                                        id="monthly_year_select"
                                        value={selectedYear}
                                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                    >
                                        {yearOptions.map(y => (
                                            <option key={y} value={y}>{y}</option>
                                        ))}
                                    </select>
                                    <label htmlFor="monthly_year_select">Select Year</label>
                                </div>
                            </div>

                            {/* Month Selector Dropdown */}
                            <div style={{ width: "160px" }}>
                                <div className="form-floating">
                                    <select
                                        className="form-select fw-bold"
                                        id="monthly_month_select"
                                        value={selectedMonth}
                                        onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                                    >
                                        {MONTHS.map(m => (
                                            <option key={m.value} value={m.value}>{m.label}</option>
                                        ))}
                                    </select>
                                    <label htmlFor="monthly_month_select">Select Month</label>
                                </div>
                            </div>

                            {/* Search Bar for Monthly Summary */}
                            <div style={{ width: "220px" }}>
                                <div className="form-floating">
                                    <input
                                        className="form-control"
                                        id="monthly_search"
                                        value={monthlySearchText}
                                        onChange={(e) => setMonthlySearchText(e.target.value)}
                                        placeholder="Search Product"
                                    />
                                    <label htmlFor="monthly_search">Search Product</label>
                                </div>
                            </div>

                            {/* Print Button */}
                            <button
                                type="button"
                                className="btn btn-dark p-3"
                                onClick={handlePrint}
                                style={{ height: "58px" }}
                            >
                                <i className="bi bi-printer me-1"></i> Print
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="d-flex mb-4 border-bottom justify-content-between align-items-center flex-wrap pb-3" style={{ width: "100%" }}>
                        <h5 className="text-start w-auto fw-bold mb-0 text-uppercase">
                            {activeTab === "stock_summary" ? "Stock Summary (By Period)" : "Finished Product Report"}
                        </h5>
                        <div className="d-flex gap-2 align-items-center flex-wrap ms-auto">
                            {/* Search Bar */}
                            <div style={{ width: "220px" }}>
                                <div className="form-floating">
                                    <input
                                        className="form-control"
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        placeholder="Search"
                                    />
                                    <label>Search Product</label>
                                </div>
                            </div>

                            {/* Filter by Status (Only in Batch Process Report) */}
                            {activeTab === "batch_report" && (
                                <div style={{ width: "200px" }}>
                                    <div className="form-floating">
                                        <select
                                            className="form-select"
                                            value={selectedStatus}
                                            onChange={(e) => setSelectedStatus(e.target.value)}
                                        >
                                            <option value="">All Statuses</option>
                                            <option value="3">Completed</option>
                                            <option value="4">Distributed</option>
                                        </select>
                                        <label>Process Status</label>
                                    </div>
                                </div>
                            )}

                            {/* Date Filters */}
                            <div style={{ width: "160px" }}>
                                <FormattedDateInput
                                    id="from_date_filter"
                                    label="From Date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                />
                            </div>
                            <div style={{ width: "160px" }}>
                                <FormattedDateInput
                                    id="to_date_filter"
                                    label="To Date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                />
                            </div>
                            <button
                                type="button"
                                className="btn btn-primary p-3"
                                onClick={fetchData}
                                style={{ height: "58px" }}
                            >
                                Filter
                            </button>

                            {/* Print Button */}
                            <button
                                type="button"
                                className="btn btn-dark p-3"
                                onClick={handlePrint}
                                style={{ height: "58px" }}
                            >
                                <i className="bi bi-printer me-1"></i> Print
                            </button>
                        </div>
                    </div>
                )}

                {/* KPI Highlight Cards */}
                {activeTab === "date_summary" && (
                    <div className="row g-3 mb-4 text-start">
                        <div className="col-12 col-md-4">
                            <div className="card border-0 shadow-sm p-3 rounded-3" style={{ background: "linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 100%)", borderLeft: "5px solid #0284c7" }}>
                                <div className="text-muted small fw-bold text-uppercase">Products In Stock</div>
                                <div className="fs-3 fw-bold text-primary mt-1">{filteredDateStock.length}</div>
                                <div className="small text-muted mt-1">Available on {moment(selectedDate).format('DD/MM/YYYY')}</div>
                            </div>
                        </div>
                        <div className="col-12 col-md-4">
                            <div className="card border-0 shadow-sm p-3 rounded-3" style={{ background: "linear-gradient(135deg, #dcfce7 0%, #f0fdf4 100%)", borderLeft: "5px solid #16a34a" }}>
                                <div className="text-muted small fw-bold text-uppercase">Total Available Stock</div>
                                <div className="fs-3 fw-bold text-success mt-1">{totalDateClosingStock.toFixed(3)} kg</div>
                                <div className="small text-muted mt-1">Net stock weight</div>
                            </div>
                        </div>
                        <div className="col-12 col-md-4">
                            <div className="card border-0 shadow-sm p-3 rounded-3" style={{ background: "linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%)", borderLeft: "5px solid #d97706" }}>
                                <div className="text-muted small fw-bold text-uppercase">Total Stock Costing</div>
                                <div className="fs-3 fw-bold text-dark mt-1">
                                    ₹{totalDateClosingCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                                <div className="small text-muted mt-1">Cost valuation</div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === "monthly_summary" && (
                    <div className="row g-3 mb-4 text-start">
                        <div className="col-12 col-md-4">
                            <div className="card border-0 shadow-sm p-3 rounded-3" style={{ background: "linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 100%)", borderLeft: "5px solid #0284c7" }}>
                                <div className="text-muted small fw-bold text-uppercase">Products In Month</div>
                                <div className="fs-3 fw-bold text-primary mt-1">{filteredMonthlyStock.length}</div>
                                <div className="small text-muted mt-1">For {MONTHS.find(m => m.value === selectedMonth)?.label} {selectedYear}</div>
                            </div>
                        </div>
                        <div className="col-12 col-md-4">
                            <div className="card border-0 shadow-sm p-3 rounded-3" style={{ background: "linear-gradient(135deg, #dcfce7 0%, #f0fdf4 100%)", borderLeft: "5px solid #16a34a" }}>
                                <div className="text-muted small fw-bold text-uppercase">Monthly Closing Stock</div>
                                <div className="fs-3 fw-bold text-success mt-1">{totalMonthlyClosingStock.toFixed(3)} kg</div>
                                <div className="small text-muted mt-1">End of month total</div>
                            </div>
                        </div>
                        <div className="col-12 col-md-4">
                            <div className="card border-0 shadow-sm p-3 rounded-3" style={{ background: "linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%)", borderLeft: "5px solid #d97706" }}>
                                <div className="text-muted small fw-bold text-uppercase">Monthly Closing Cost</div>
                                <div className="fs-3 fw-bold text-dark mt-1">
                                    ₹{totalMonthlyClosingCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                                <div className="small text-muted mt-1">Closing valuation</div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="d-flex justify-content-between mb-3 px-2">
                    <div>
                        <strong>Total Records:</strong> {
                            activeTab === "date_summary"
                                ? filteredDateStock.length
                                : activeTab === "monthly_summary" 
                                    ? filteredMonthlyStock.length 
                                    : activeTab === "stock_summary" 
                                        ? stockSummary.length 
                                        : totalItems
                        }
                    </div>
                    {activeTab === "date_summary" && (
                        <div className="text-muted fw-semibold">
                            Showing As Of: <span className="text-primary">{moment(selectedDate).format('DD/MM/YYYY')}</span>
                        </div>
                    )}
                    {activeTab === "monthly_summary" && (
                        <div className="text-muted fw-semibold">
                            Showing: <span className="text-primary">{MONTHS.find(m => m.value === selectedMonth)?.label} {selectedYear}</span>
                        </div>
                    )}
                </div>

                {/* Table View */}
                <div className="table-responsive text-center my-2 w-100 position-relative">
                    {/* TAB 1: DATE WISE SUMMARY */}
                    {activeTab === "date_summary" ? (
                        <table className="table table-sm table-bordered table-hover align-middle">
                            <thead className="table-dark">
                                <tr>
                                    <th style={{ width: "70px" }}>S.No</th>
                                    <th className="text-start px-3">Product Name</th>
                                    <th style={{ width: "190px" }}>Available Stock</th>
                                    <th style={{ width: "200px" }}>Stock Costing (INR)</th>
                                    <th className="text-start px-3" style={{ width: "350px" }}>Package Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dateLoading ? (
                                    <tr>
                                        <td colSpan="5" className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status" style={{ width: "2.5rem", height: "2.5rem" }}>
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <div className="mt-2 text-muted fw-semibold">Loading date-wise closing stock data...</div>
                                        </td>
                                    </tr>
                                ) : filteredDateStock.length > 0 ? (
                                    filteredDateStock.map((item, index) => {
                                        const hasPackages = item.packages && item.packages.length > 0;
                                        const isExpanded = !!expandedDateRows[item.p_id || index];
                                        return (
                                            <React.Fragment key={item.p_id || index}>
                                                <tr 
                                                    onClick={() => hasPackages && toggleDateRow(item.p_id || index)}
                                                    style={{ cursor: hasPackages ? "pointer" : "default" }}
                                                    title={hasPackages ? "Click to view package breakdown" : ""}
                                                >
                                                    <td>
                                                        {hasPackages && (
                                                            <span className="me-1 text-primary small">
                                                                {isExpanded ? "▼" : "▶"}
                                                            </span>
                                                        )}
                                                        {index + 1}
                                                    </td>
                                                    <td className="fw-semibold text-start px-3">
                                                        {item.product_title || "-"}
                                                    </td>
                                                    <td className="text-primary fw-bold">
                                                        {parseFloat(item.closing_stock || 0).toFixed(3)} kg
                                                    </td>
                                                    <td className="fw-bold">
                                                        ₹{parseFloat(item.closing_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </td>
                                                    <td className="text-start px-3">
                                                        {hasPackages ? (
                                                            <div className="d-flex flex-wrap gap-1">
                                                                {item.packages.map((pkg, pIdx) => (
                                                                    <span key={pIdx} className="badge bg-light text-dark border px-2 py-1" style={{ fontSize: "11px" }}>
                                                                        {pkg.package_title}: <strong>{pkg.packets}</strong> pkts ({pkg.stock_kg} kg)
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted small">-</span>
                                                        )}
                                                    </td>
                                                </tr>
                                                {isExpanded && hasPackages && (
                                                    <tr>
                                                        <td colSpan="5" className="p-0 bg-light">
                                                            <div className="p-3">
                                                                <div className="fw-bold small text-muted text-uppercase mb-2 text-start">
                                                                    Package Breakdown for {item.product_title}:
                                                                </div>
                                                                <table className="table table-sm table-bordered m-0 bg-white">
                                                                    <thead className="table-secondary small">
                                                                        <tr>
                                                                            <th className="text-start px-3">Package Size / Type</th>
                                                                            <th>Available Packets</th>
                                                                            <th>Total Weight (kg)</th>
                                                                            <th>Unit Cost (₹)</th>
                                                                            <th>Total Cost (₹)</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {item.packages.map((pkg, pIdx) => (
                                                                            <tr key={pIdx}>
                                                                                <td className="text-start px-3 fw-semibold">{pkg.package_title}</td>
                                                                                <td>{pkg.packets}</td>
                                                                                <td className="text-primary fw-bold">{pkg.stock_kg} kg</td>
                                                                                <td>₹{parseFloat(pkg.unit_cost || 0).toFixed(2)}</td>
                                                                                <td className="fw-bold">₹{parseFloat(pkg.cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="text-center py-4 text-muted">
                                            No closing stock records found for {moment(selectedDate).format('DD/MM/YYYY')}.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                            {!dateLoading && filteredDateStock.length > 0 && (
                                <tfoot className="table-secondary fw-bold">
                                    <tr>
                                        <td colSpan="2" className="text-end px-3 fs-6">Total:</td>
                                        <td className="text-primary fs-6">
                                            {totalDateClosingStock.toFixed(3)} kg
                                        </td>
                                        <td className="fs-6">
                                            ₹{totalDateClosingCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    ) : activeTab === "monthly_summary" ? (
                        /* TAB 2: MONTHLY SUMMARY */
                        <table className="table table-sm table-bordered table-hover align-middle">
                            <thead className="table-dark">
                                <tr>
                                    <th style={{ width: "70px" }}>S.No</th>
                                    <th className="text-start px-3">Product Name</th>
                                    <th style={{ width: "190px" }}>Closing Stock</th>
                                    <th style={{ width: "200px" }}>Closing Cost (INR)</th>
                                    <th className="text-start px-3" style={{ width: "350px" }}>Package Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {monthlyLoading ? (
                                    <tr>
                                        <td colSpan="5" className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status" style={{ width: "2.5rem", height: "2.5rem" }}>
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <div className="mt-2 text-muted fw-semibold">Loading monthly closing stock data...</div>
                                        </td>
                                    </tr>
                                ) : filteredMonthlyStock.length > 0 ? (
                                    filteredMonthlyStock.map((item, index) => {
                                        const hasPackages = item.packages && item.packages.length > 0;
                                        const isExpanded = !!expandedMonthlyRows[item.p_id || index];
                                        return (
                                            <React.Fragment key={item.p_id || index}>
                                                <tr 
                                                    onClick={() => hasPackages && toggleMonthlyRow(item.p_id || index)}
                                                    style={{ cursor: hasPackages ? "pointer" : "default" }}
                                                    title={hasPackages ? "Click to view package breakdown" : ""}
                                                >
                                                    <td>
                                                        {hasPackages && (
                                                            <span className="me-1 text-primary small">
                                                                {isExpanded ? "▼" : "▶"}
                                                            </span>
                                                        )}
                                                        {index + 1}
                                                    </td>
                                                    <td className="fw-semibold text-start px-3">{item.product_title || "-"}</td>
                                                    <td className="text-primary fw-bold">
                                                        {parseFloat(item.closing_stock || 0).toFixed(3)} kg
                                                    </td>
                                                    <td className="fw-bold">
                                                        ₹{parseFloat(item.closing_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </td>
                                                    <td className="text-start px-3">
                                                        {hasPackages ? (
                                                            <div className="d-flex flex-wrap gap-1">
                                                                {item.packages.map((pkg, pIdx) => (
                                                                    <span key={pIdx} className="badge bg-light text-dark border px-2 py-1" style={{ fontSize: "11px" }}>
                                                                        {pkg.package_title}: <strong>{pkg.packets}</strong> pkts ({pkg.stock_kg} kg)
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted small">-</span>
                                                        )}
                                                    </td>
                                                </tr>
                                                {isExpanded && hasPackages && (
                                                    <tr>
                                                        <td colSpan="5" className="p-0 bg-light">
                                                            <div className="p-3">
                                                                <div className="fw-bold small text-muted text-uppercase mb-2 text-start">
                                                                    Package Breakdown for {item.product_title}:
                                                                </div>
                                                                <table className="table table-sm table-bordered m-0 bg-white">
                                                                    <thead className="table-secondary small">
                                                                        <tr>
                                                                            <th className="text-start px-3">Package Size / Type</th>
                                                                            <th>Available Packets</th>
                                                                            <th>Total Weight (kg)</th>
                                                                            <th>Unit Cost (₹)</th>
                                                                            <th>Total Cost (₹)</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {item.packages.map((pkg, pIdx) => (
                                                                            <tr key={pIdx}>
                                                                                <td className="text-start px-3 fw-semibold">{pkg.package_title}</td>
                                                                                <td>{pkg.packets}</td>
                                                                                <td className="text-primary fw-bold">{pkg.stock_kg} kg</td>
                                                                                <td>₹{parseFloat(pkg.unit_cost || 0).toFixed(2)}</td>
                                                                                <td className="fw-bold">₹{parseFloat(pkg.cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="text-center py-4 text-muted">
                                            No closing stock records found for {MONTHS.find(m => m.value === selectedMonth)?.label} {selectedYear}.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                            {!monthlyLoading && filteredMonthlyStock.length > 0 && (
                                <tfoot className="table-secondary fw-bold">
                                    <tr>
                                        <td colSpan="2" className="text-end px-3 fs-6">Total:</td>
                                        <td className="text-primary fs-6">
                                            {totalMonthlyClosingStock.toFixed(3)} kg
                                        </td>
                                        <td className="fs-6">
                                            ₹{totalMonthlyClosingCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    ) : activeTab === "stock_summary" ? (
                        /* TAB 3: STOCK SUMMARY BY PERIOD */
                        <table className="table table-sm table-bordered table-hover align-middle">
                            <thead className="table-dark">
                                <tr>
                                    <th>S.No</th>
                                    <th>Product Name</th>
                                    <th>Opening Stock</th>
                                    <th>Processed Stock</th>
                                    <th>Sold/Distributed</th>
                                    <th>Closing Stock</th>
                                    <th>Closing Cost</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stockSummary.length > 0 ? (
                                    stockSummary.map((item, index) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td className="fw-semibold text-start px-3">{item.product_title || "-"}</td>
                                            <td className="fw-bold">{item.opening_stock} kg</td>
                                            <td className="text-success fw-bold">+{item.processed_stock} kg</td>
                                            <td className="text-danger fw-bold">-{item.sold_stock} kg</td>
                                            <td className="text-primary fw-bold">{item.closing_stock} kg</td>
                                            <td className="fw-bold">₹{parseFloat(item.closing_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center py-4 text-muted">
                                            {(!fromDate || !toDate) ? "Please select From and To dates to view Stock Summary." : "No stock data found for the selected period."}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                            {stockSummary.length > 0 && (
                                <tfoot>
                                    <tr className="table-light">
                                        <th colSpan="6" className="text-end px-3">Total Closing Cost:</th>
                                        <th className="fw-bold text-start">
                                            ₹{stockSummary.reduce((acc, item) => acc + parseFloat(item.closing_cost || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </th>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    ) : (
                        /* TAB 4: BATCH PROCESS REPORT */
                        <table className="table table-sm table-bordered table-hover align-middle">
                            <thead className="table-dark">
                                <tr>
                                    <th>S.No</th>
                                    <th>Product Name</th>
                                    <th>Processed Quantity</th>
                                    <th>Total Costing</th>
                                    <th>Status</th>
                                    <th>Avl Qty</th>
                                    <th>Created Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groupedData.length > 0 ? (
                                    groupedData.map((item, index) => {
                                        const qty = parseFloat(item.manufactured_qty || item.desired_qty || 0);
                                        const looseProduct = parseFloat(item.avl_qty || 0);

                                        return (
                                            <React.Fragment key={item.id || index}>
                                                <tr 
                                                    onClick={() => toggleRow(item.id || index)}
                                                    style={{ cursor: "pointer" }}
                                                    title="Click to view batch details"
                                                >
                                                    <td>
                                                        {expandedRows[item.id || index] ? "▼" : "▶"} {index + 1}
                                                    </td>
                                                    <td className="fw-semibold text-start px-3">
                                                        {item.product_title || "-"}
                                                        {item.isLoose && (
                                                            <span className="text-muted fw-normal ms-1" style={{ fontSize: "12px" }}>
                                                                ({(item.product_title || "").toLowerCase().includes("powder") ? "loose powder" : "loose product"})
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>{qty.toFixed(2)} {item.unit || "kg"}</td>
                                                    <td className="fw-bold">{Number(item.overall_price || 0).toFixed(3)}</td>
                                                    <td className={getStatusClass(item.status)}>{getStatusLabel(item.status)}</td>
                                                    <td className="text-end px-3 fw-bold text-primary">
                                                        {parseFloat(item.status) >= 3 ? (
                                                            `${looseProduct.toFixed(2)} ${item.unit || "kg"}`
                                                        ) : (
                                                            <span className="text-muted fw-normal">-</span>
                                                        )}
                                                    </td>
                                                    <td>{item.create_date && moment(item.create_date).isValid() ? moment(item.create_date).format('DD/MM/YYYY') : (item.create_date?.split('T')[0] || "-")}</td>
                                                </tr>
                                                {expandedRows[item.id || index] && item.batches && item.batches.length > 0 && (
                                                    <tr>
                                                        <td colSpan="7" className="p-0">
                                                            <table className="table table-sm table-bordered m-0" style={{ backgroundColor: "#f8f9fa" }}>
                                                                <thead className="table-secondary">
                                                                    <tr>
                                                                        <th>Batch No</th>
                                                                        <th>Quantity</th>
                                                                        <th>Costing</th>
                                                                        <th>Date</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {item.batches.map((batchItem, bIndex) => (
                                                                        <tr key={bIndex}>
                                                                            <td>{batchItem.batch_no || "-"}</td>
                                                                            <td>{parseFloat(batchItem.manufactured_qty || batchItem.desired_qty || 0).toFixed(2)} {batchItem.unit || "kg"}</td>
                                                                            <td>{Number(batchItem.overall_price || 0).toFixed(3)}</td>
                                                                            <td>{batchItem.create_date && moment(batchItem.create_date).isValid() ? moment(batchItem.create_date).format('DD/MM/YYYY') : (batchItem.create_date?.split('T')[0] || "-")}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center py-4 text-muted">
                                            No finished product batches found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                            <tfoot>
                                <tr className="table-light fw-bold" style={{ fontSize: "15px" }}>
                                    <td colSpan="3" className="text-end pe-3 text-uppercase">Total Amount:</td>
                                    <td className="text-center">{Number(totalOverallPrice).toFixed(3)}</td>
                                    <td colSpan="3"></td>
                                </tr>
                            </tfoot>
                        </table>
                    )}
                </div>

                {/* Printable Report (Hidden in normal screen view) */}
                <div id="printable-finished-product-report" style={{ display: 'none' }}>
                    {activeTab === "date_summary" ? (
                        <div className="print-container">
                            <div className="print-header">
                                <h1>{companyInfo.name}</h1>
                                <p>{companyInfo.address}</p>
                                <p>Phone: {companyInfo.phone} | Email: {companyInfo.email}</p>
                                <h2 style={{ marginTop: '20px', textTransform: 'uppercase' }}>
                                    Finished Product Closing Stock Report — As of {moment(selectedDate).format('DD/MM/YYYY')}
                                </h2>
                            </div>

                            <div className="report-info">
                                <div>
                                    <strong>Generated On:</strong> {moment().format('DD/MM/YYYY')} at {moment().format('hh:mm A')}
                                </div>
                                <div>
                                    <strong>As of Date:</strong> {moment(selectedDate).format('DD/MM/YYYY')}
                                </div>
                                <div>
                                    <strong>Total Products:</strong> {filteredDateStock.length}
                                </div>
                            </div>

                            <table className="report-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '8%' }}>S.No</th>
                                        <th style={{ width: '42%' }}>Product Name</th>
                                        <th style={{ width: '25%' }}>Available / Closing Stock</th>
                                        <th style={{ width: '25%' }}>Closing Cost (INR)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredDateStock.length > 0 ? (
                                        filteredDateStock.map((item, index) => (
                                            <React.Fragment key={index}>
                                                <tr>
                                                    <td>{index + 1}</td>
                                                    <td style={{ fontWeight: 'bold' }}>{item.product_title || "-"}</td>
                                                    <td style={{ fontWeight: 'bold', color: '#004085' }}>
                                                        {parseFloat(item.closing_stock || 0).toFixed(3)} kg
                                                    </td>
                                                    <td style={{ fontWeight: 'bold' }}>
                                                        ₹{parseFloat(item.closing_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </td>
                                                </tr>
                                                {item.packages && item.packages.length > 0 && (
                                                    <tr>
                                                        <td></td>
                                                        <td colSpan="3" style={{ padding: '4px 12px', fontSize: '9pt', color: '#555', backgroundColor: '#fafafa' }}>
                                                            <em>Breakdown: </em>
                                                            {item.packages.map(p => `${p.package_title}: ${p.packets} pkts (${p.stock_kg} kg - ₹${p.cost})`).join(' | ')}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" style={{ textAlign: 'center' }}>
                                                No closing stock records found for {moment(selectedDate).format('DD/MM/YYYY')}.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <th colSpan="2" style={{ textAlign: 'right', paddingRight: '10px' }}>Total:</th>
                                        <th style={{ color: '#004085' }}>{totalDateClosingStock.toFixed(3)} kg</th>
                                        <th>₹{totalDateClosingCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</th>
                                    </tr>
                                </tfoot>
                            </table>

                            <div className="report-totals">
                                <h3>Report Summary</h3>
                                <div className="total-row">
                                    <span>Selected Date:</span>
                                    <span className="total-amount">{moment(selectedDate).format('DD/MM/YYYY')}</span>
                                </div>
                                <div className="total-row">
                                    <span>Total Available Stock:</span>
                                    <span className="total-amount">{totalDateClosingStock.toFixed(3)} kg</span>
                                </div>
                                <div className="total-row">
                                    <span>Total Stock Costing:</span>
                                    <span className="total-amount">₹{totalDateClosingCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                            </div>

                            <div className="footer">
                                <p>ZTR Masala Inventory Management System</p>
                            </div>
                        </div>
                    ) : activeTab === "monthly_summary" ? (
                        <div className="print-container">
                            <div className="print-header">
                                <h1>{companyInfo.name}</h1>
                                <p>{companyInfo.address}</p>
                                <p>Phone: {companyInfo.phone} | Email: {companyInfo.email}</p>
                                <h2 style={{ marginTop: '20px', textTransform: 'uppercase' }}>
                                    Monthly Closing Stock Report — {MONTHS.find(m => m.value === selectedMonth)?.label.toUpperCase()} {selectedYear}
                                </h2>
                            </div>

                            <div className="report-info">
                                <div>
                                    <strong>Generated On:</strong> {moment().format('DD/MM/YYYY')} at {moment().format('hh:mm A')}
                                </div>
                                <div>
                                    <strong>Month:</strong> {MONTHS.find(m => m.value === selectedMonth)?.label} {selectedYear}
                                </div>
                                <div>
                                    <strong>Total Products:</strong> {filteredMonthlyStock.length}
                                </div>
                            </div>

                            <table className="report-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '10%' }}>S.No</th>
                                        <th style={{ width: '45%' }}>Product Name</th>
                                        <th style={{ width: '25%' }}>Closing Stock</th>
                                        <th style={{ width: '20%' }}>Closing Cost (INR)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredMonthlyStock.length > 0 ? (
                                        filteredMonthlyStock.map((item, index) => (
                                            <React.Fragment key={index}>
                                                <tr>
                                                    <td>{index + 1}</td>
                                                    <td style={{ fontWeight: 'bold' }}>{item.product_title || "-"}</td>
                                                    <td style={{ fontWeight: 'bold', color: '#004085' }}>
                                                        {parseFloat(item.closing_stock || 0).toFixed(3)} kg
                                                    </td>
                                                    <td style={{ fontWeight: 'bold' }}>
                                                        ₹{parseFloat(item.closing_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </td>
                                                </tr>
                                                {item.packages && item.packages.length > 0 && (
                                                    <tr>
                                                        <td></td>
                                                        <td colSpan="3" style={{ padding: '4px 12px', fontSize: '9pt', color: '#555', backgroundColor: '#fafafa' }}>
                                                            <em>Breakdown: </em>
                                                            {item.packages.map(p => `${p.package_title}: ${p.packets} pkts (${p.stock_kg} kg - ₹${p.cost})`).join(' | ')}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" style={{ textAlign: 'center' }}>
                                                No closing stock records found for {MONTHS.find(m => m.value === selectedMonth)?.label} {selectedYear}.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <th colSpan="2" style={{ textAlign: 'right', paddingRight: '10px' }}>Total:</th>
                                        <th style={{ color: '#004085' }}>{totalMonthlyClosingStock.toFixed(3)} kg</th>
                                        <th>₹{totalMonthlyClosingCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</th>
                                    </tr>
                                </tfoot>
                            </table>

                            <div className="report-totals">
                                <h3>Report Summary</h3>
                                <div className="total-row">
                                    <span>Selected Month:</span>
                                    <span className="total-amount">{MONTHS.find(m => m.value === selectedMonth)?.label} {selectedYear}</span>
                                </div>
                                <div className="total-row">
                                    <span>Total Closing Stock:</span>
                                    <span className="total-amount">{totalMonthlyClosingStock.toFixed(3)} kg</span>
                                </div>
                                <div className="total-row">
                                    <span>Total Closing Cost:</span>
                                    <span className="total-amount">₹{totalMonthlyClosingCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                            </div>

                            <div className="footer">
                                <p>ZTR Masala Inventory Management System</p>
                            </div>
                        </div>
                    ) : (
                        <div className="print-container">
                            <div className="print-header">
                                <h1>{companyInfo.name}</h1>
                                <p>{companyInfo.address}</p>
                                <p>Phone: {companyInfo.phone} | Email: {companyInfo.email}</p>
                                <h2 style={{ marginTop: '20px', textTransform: 'uppercase' }}>Finished Product Stock Report</h2>
                            </div>

                            <div className="report-info">
                                <div>
                                    <strong>Generated On:</strong> {moment().format('DD/MM/YYYY')} at {moment().format('hh:mm A')}
                                </div>
                                <div>
                                    <strong>Total Records:</strong> {totalItems}
                                </div>
                            </div>

                            <table className="report-table">
                                <thead>
                                    <tr>
                                        <th>S.No</th>
                                        <th>Product Name</th>
                                        <th>Processed Quantity</th>
                                        <th>Total Costing</th>
                                        <th>Status</th>
                                        <th>Available Qty</th>
                                        <th>Created Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupedData.length > 0 ? (
                                        groupedData.map((item, index) => {
                                            const qty = parseFloat(item.manufactured_qty || item.desired_qty || 0);
                                            const looseProduct = parseFloat(item.avl_qty || 0);

                                            return (
                                                <tr key={item.id || index}>
                                                    <td>{index + 1}</td>
                                                    <td style={{ fontWeight: 'bold' }}>
                                                        {item.product_title || "-"}
                                                        {item.isLoose && (
                                                            <span style={{ fontWeight: 'normal', color: '#6c757d', marginLeft: '5px', fontSize: '11px' }}>
                                                                ({(item.product_title || "").toLowerCase().includes("powder") ? "loose powder" : "loose product"})
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>{qty.toFixed(2)} {item.unit || "kg"}</td>
                                                    <td>{Number(item.overall_price || 0).toFixed(3)}</td>
                                                    <td>{getStatusLabel(item.status)}</td>
                                                    <td style={{ fontWeight: 'bold', color: '#004085' }}>
                                                        {parseFloat(item.status) >= 3 ? (
                                                            `${looseProduct.toFixed(2)} ${item.unit || "kg"}`
                                                        ) : (
                                                            "-"
                                                        )}
                                                    </td>
                                                    <td>{item.create_date && moment(item.create_date).isValid() ? moment(item.create_date).format('DD/MM/YYYY') : (item.create_date?.split('T')[0] || "-")}</td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="6" style={{ textAlign: 'center' }}>
                                                No finished products found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <th colSpan="3" style={{ textAlign: 'right', paddingRight: '10px' }}>Total Amount:</th>
                                        <th>{Number(totalOverallPrice).toFixed(3)}</th>
                                        <th colSpan="3"></th>
                                    </tr>
                                </tfoot>
                            </table>

                            <div className="report-totals">
                                <h3>Report Summary</h3>
                                <div className="total-row">
                                    <span>Total Process Records:</span>
                                    <span className="total-amount">{totalItems}</span>
                                </div>
                                <div className="total-row">
                                    <span>Total Costing Amount:</span>
                                    <span className="total-amount">{Number(totalOverallPrice).toFixed(3)}</span>
                                </div>
                            </div>

                            <div className="footer">
                                <p>ZTR Masala Inventory Management System</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FinishedProductReport;
